/**
 * reserve-to-buy.js
 *
 * Purpose:
 * - On product page: capture product/variant info from .lh-reserve data attributes when user clicks "Reserve to Buy"
 * - Save payload to sessionStorage (so it persists across navigation, but clears on browser close)
 * - On contact page: auto-fill the Shopify contact form textarea with the saved payload
 *
 * Assumptions:
 * - Product template includes a wrapper element with [data-reserve-root] (your .lh-reserve div)
 * - CTA button/link has [data-reserve-to-buy]
 * - Contact page lives at /pages/contact (but we also auto-detect any page that contains the Shopify contact form)
 */

(function () {
  "use strict";

  var STORAGE_KEY = "lh_reserve_to_buy_payload_v1";

  // ---------- Helpers ----------
  function formatMoneyFromCents(cents, currencyCode) {
    // cents might be string or number
    var n = Number(cents);
    if (!isFinite(n)) return "";
    var amount = n / 100;

    // Use browser Intl when available
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode || "USD",
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      // Fallback
      return (currencyCode ? currencyCode + " " : "$") + amount.toFixed(2);
    }
  }

  function safeGetAttr(el, name) {
    if (!el) return "";
    var v = el.getAttribute(name);
    return v == null ? "" : String(v);
  }

 function buildReservationMessage(payload) {
  var lines = [];

  lines.push("Reservation request (Reserve to Buy)");
  lines.push("");
  lines.push("Product: " + (payload.productTitle || ""));
  if (payload.variantTitle && payload.variantTitle !== "Default Title") {
    lines.push("Variant: " + payload.variantTitle);
  }
  if (payload.variantSku) {
    lines.push("SKU: " + payload.variantSku);
  }
  if (payload.productUrl) {
    lines.push("Product link: " + payload.productUrl);
  }
  if (payload.priceFormatted) {
    lines.push("Price: " + payload.priceFormatted);
  }
  if (payload.currency) {
    lines.push("Currency: " + payload.currency);
  }

  return lines.join("\n");
}

  function readPayloadFromReserveRoot(reserveRootEl) {
    var currency = safeGetAttr(reserveRootEl, "data-currency");
    var priceCents = safeGetAttr(reserveRootEl, "data-variant-price-cents");

    // Build absolute product URL if the template provided relative
    var productUrl = safeGetAttr(reserveRootEl, "data-product-url");
    if (productUrl && productUrl.indexOf("http") !== 0) {
      productUrl = window.location.origin + productUrl;
    }

    var payload = {
      capturedAt: new Date().toISOString(),
      contactUrl: safeGetAttr(reserveRootEl, "data-contact-url") || "/pages/contact",

      productTitle: safeGetAttr(reserveRootEl, "data-product-title"),
      productHandle: safeGetAttr(reserveRootEl, "data-product-handle"),
      productUrl: productUrl,

      variantTitle: safeGetAttr(reserveRootEl, "data-variant-title"),
      variantSku: safeGetAttr(reserveRootEl, "data-variant-sku"),
      variantId: safeGetAttr(reserveRootEl, "data-variant-id"),

      variantPriceCents: priceCents,
      currency: currency,
      priceFormatted: formatMoneyFromCents(priceCents, currency),
    };

    payload.message = buildReservationMessage(payload);

    return payload;
  }

  function savePayload(payload) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (e) {
      return false;
    }
  }

  function loadPayload() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearPayload() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function isProbablyContactPage() {
    // Covers /pages/contact plus any template that includes the Shopify contact form.
    if (window.location.pathname && window.location.pathname.indexOf("/pages/contact") === 0) return true;

    // Shopify contact form often has form[action*="/contact"] with input name="contact[email]" etc.
    var form = document.querySelector('form[action*="/contact"]');
    if (!form) return false;

    var hasEmail = !!form.querySelector('input[name="contact[email]"], input[name="contact[email_or_phone]"]');
    var hasBody = !!form.querySelector('textarea[name="contact[body]"], textarea[name="contact[comment]"]');

    return hasEmail && hasBody;
  }

  function findContactTextarea() {
    // Common defaults:
    // - textarea[name="contact[body]"] (most Shopify themes)
    // - sometimes textarea[name="contact[comment]"] (custom)
    return (
      document.querySelector('form[action*="/contact"] textarea[name="contact[body]"]') ||
      document.querySelector('form[action*="/contact"] textarea[name="contact[comment]"]') ||
      document.querySelector('textarea[name="contact[body]"]') ||
      document.querySelector('textarea[name="contact[comment]"]')
    );
  }

  function injectMessageIntoContactForm(payload) {
    var textarea = findContactTextarea();
    if (!textarea) return false;

    // Do not overwrite if user already typed something meaningful
    var existing = (textarea.value || "").trim();
    if (existing.length > 0) {
      // If you prefer always-prepend, change this behavior.
      return false;
    }

    textarea.value = payload.message || "";
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    textarea.dispatchEvent(new Event("change", { bubbles: true }));

    // Scroll to the form area nicely
    try {
      textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      textarea.focus({ preventScroll: true });
    } catch (e) {}

    return true;
  }

  // ---------- Product page listener ----------
  function initReserveButtonCapture() {
    var reserveRootEl = document.querySelector("[data-reserve-root]");
    if (!reserveRootEl) return;

    // Delegate click on anything with [data-reserve-to-buy] inside reserveRootEl
    reserveRootEl.addEventListener("click", function (e) {
      var target = e.target;
      if (!target) return;

      var cta = target.closest("[data-reserve-to-buy]");
      if (!cta) return;

      var payload = readPayloadFromReserveRoot(reserveRootEl);
      savePayload(payload);

      // If CTA is a link, allow navigation as normal.
      // If CTA ever becomes a button, we can programmatically route:
      // window.location.href = payload.contactUrl;
    });
  }

  // ---------- Contact page autofill ----------
  function initContactAutofill() {
    if (!isProbablyContactPage()) return;

    var payload = loadPayload();
    if (!payload || !payload.message) return;

    // Wait a tick in case theme renders form late
    window.requestAnimationFrame(function () {
      var injected = injectMessageIntoContactForm(payload);

      // Clear payload even if not injected, to avoid stale data.
      // If you want to keep it until successful inject, change this.
      clearPayload();

      // Optional: if it didn't inject because textarea already had content,
      // you could append it instead. Leaving strict for now.
      void injected;
    });
  }

  // ---------- Boot ----------
  function boot() {
    initReserveButtonCapture();
    initContactAutofill();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();